import {
    type AccountBalancesWidgetProps,
    AnalyticsUserEvent,
    type CardDetailsWidgetProps,
    ChartKind,
    type ChartWidgetProps,
    type EmailSupportWidgetProps,
    type FreeToSpendWidgetProps,
    type IncomeSplitWidgetProps,
    type InlineDataTableWidgetProps,
    type MultiSelectInputWidgetProps,
    type SendToGuideWidgetProps,
    type SpendGoalsWidgetProps,
    type TransactionsWidgetProps,
    WidgetType,
} from '@fruitful/types';

import { captureException } from '../../logger/Logger';

type PropsForTracking = Record<string, string | number | boolean>;

interface WidgetRenderAnalytics {
    eventType: AnalyticsUserEvent | null;
    getPropsForTracking: (
        props: Record<string, unknown> | null,
    ) => PropsForTracking;
}

/**
 * Widget-specific first-render analytics. Keep values flat and non-PII.
 * Use `null` (or `eventType: null`) when there is nothing meaningful to log.
 */
const WIDGET_RENDER_ANALYTICS: Record<
    WidgetType,
    WidgetRenderAnalytics | null
> = {
    [WidgetType.Chart]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as ChartWidgetProps;
            return {
                widgetTitle: props.title ?? '',
                widgetChartKind: props.kind,
                widgetBarOrientation:
                    props.kind === ChartKind.Bar
                        ? (props.orientation ?? 'vertical')
                        : '',
                widgetShowLegend: props.showLegend ?? false,
                widgetSeriesCount: props.series?.length ?? 0,
            };
        },
    },
    [WidgetType.FreeToSpend]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as FreeToSpendWidgetProps;
            return {
                widgetTitle: props.title ?? '',
                widgetAccountName: props.accountName ?? '',
                widgetPeriod: props.period ?? '',
                widgetStatus:
                    props.periodRollups?.[props.period ?? 'monthly']?.status ??
                    '',
            };
        },
    },
    [WidgetType.Transactions]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_TRANSACTIONS_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as TransactionsWidgetProps;
            const groups = props.groups ?? [];
            const transactionCount = groups.reduce(
                (sum, group) => sum + (group.transactions?.length ?? 0),
                0,
            );

            return {
                widgetTitle: props.title ?? '',
                widgetGroupBy: props.groupBy ?? '',
                widgetGroupCount: groups.length,
                widgetTransactionCount: transactionCount,
            };
        },
    },
    [WidgetType.AccountBalances]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_ACCOUNT_BALANCES_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as AccountBalancesWidgetProps;
            return {
                widgetTitle: props.title ?? '',
                widgetAccountCount: props.accounts?.length ?? 0,
            };
        },
    },
    [WidgetType.IncomeSplit]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_INCOME_SPLIT_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as IncomeSplitWidgetProps;
            return {
                widgetTitle: props.title ?? '',
                widgetMode: props.mode ?? '',
            };
        },
    },
    [WidgetType.CardDetails]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_CARD_DETAILS_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as CardDetailsWidgetProps;
            return {
                // Whether the agent supplied a specific account or card
                // selection. Unresolved selections render "No card found."
                widgetHasResolvedCard:
                    props.bankAccountId != null || props.cardId != null,
            };
        },
    },
    [WidgetType.InlineDataTable]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_INLINE_DATA_TABLE_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as InlineDataTableWidgetProps;
            return {
                widgetColumnCount: props.columns?.length ?? 0,
                widgetRowCount: props.rows?.length ?? 0,
            };
        },
    },
    [WidgetType.SpendGoals]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_SPEND_GOALS_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as SpendGoalsWidgetProps;
            return {
                widgetRemainingCents: props.remainingCents,
                widgetIsConfirmed: props.isConfirmed ?? false,
            };
        },
    },
    [WidgetType.MultiSelectInput]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_MULTI_SELECT_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as MultiSelectInputWidgetProps;
            return {
                widgetResponseKey: props.responseKey,
                widgetOptionCount: props.options.length,
                widgetAllowCustomInput: props.allowCustomInput ?? false,
                widgetPreSelectedCount: props.preSelectedOptionIds?.length ?? 0,
            };
        },
    },
    [WidgetType.SliderInput]: null,
    [WidgetType.MoneyMap]: null,
    [WidgetType.GoalSummaryCard]: null,
    [WidgetType.AhaMoment]: null,
    [WidgetType.DraftedMessageButton]: null,
    [WidgetType.InfoCards]: null,
    [WidgetType.EstimatedBills]: null,
    [WidgetType.SavingsGoals]: null,
    [WidgetType.SavingsGoalAmounts]: null,
    [WidgetType.ExistingGoalsBadges]: null,
    [WidgetType.IncomeInput]: null,
    [WidgetType.DebtBalanceSliders]: null,
    [WidgetType.CreditCardRateCallout]: null,
    [WidgetType.SendToGuide]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_SEND_TO_GUIDE_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as SendToGuideWidgetProps;
            return {
                messageBody: props.messageBody ?? '',
            };
        },
    },
    [WidgetType.EmailSupport]: {
        eventType: AnalyticsUserEvent.AI_CHAT_WIDGET_EMAIL_SUPPORT_RENDERED,
        getPropsForTracking: (rawProps): PropsForTracking => {
            const props = rawProps as unknown as EmailSupportWidgetProps;
            return {
                subject: props.subject ?? '',
                body: props.body ?? '',
            };
        },
    },
};

export const getWidgetTypeTrackingInfo = (
    widgetType: string,
    props: Record<string, unknown> | null | undefined,
): { eventType: AnalyticsUserEvent; eventProps: PropsForTracking } | null => {
    const { eventType, getPropsForTracking } =
        WIDGET_RENDER_ANALYTICS[widgetType as WidgetType] ?? {};

    if (props == null || eventType == null || getPropsForTracking == null) {
        return null;
    }

    try {
        return {
            eventType,
            eventProps: getPropsForTracking(props),
        };
    } catch (error) {
        captureException(error, {
            widgetType,
        });
        return null;
    }
};
